if (!customElements.get('item-carousel')) {
  customElements.define(
    'item-carousel',
    class ItemCarousel extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLElement | null} */
        this.carousel = this.querySelector('[data-carousel]');

        /** @type {HTMLElement | null} */
        this.scrollbar = this.querySelector('[data-carousel-scrollbar]');

        /** @type {HTMLElement | null} */
        this.next = null;

        /** @type {HTMLElement | null} */
        this.prev = null;

        /** @type {import('swiper').Swiper | null} */
        this.instance = null;

        /** @type {HTMLImageElement[]} */
        this.images = Array.from(this.querySelectorAll('img'));

        /** @type {(() => void) | null} */
        this.cssModeNavigationUpdate = null;

        /** @type {number | null} */
        this.cssModeNavigationUpdateFrame = null;
      }

      connectedCallback() {
        if (this.carousel && this.scrollbar) {
          const swiper = window.swiper.Swiper;

          if (!this.carousel) return;

          this.next = this.getNavigationElement('next');
          this.prev = this.getNavigationElement('prev');

          const offsetBase = this.getAttribute('data-carousel-offset-before')
            ? Number(this.getAttribute('data-carousel-offset-before'))
            : 24;
          const offsetAfterBase = this.getAttribute('data-carousel-offset-after')
            ? Number(this.getAttribute('data-carousel-offset-after'))
            : 24;
          const slidesBase = this.getSlidesPerView('data-carousel-slides', 1.5);

          const offsetBeforeSm = this.getAttribute('data-carousel-offset-before-sm')
            ? Number(this.getAttribute('data-carousel-offset-before-sm'))
            : 32;
          const offsetAfterSm = this.getAttribute('data-carousel-offset-after-sm')
            ? Number(this.getAttribute('data-carousel-offset-after-sm'))
            : 32;
          const slidesSm = this.getSlidesPerView('data-carousel-slides-sm', 2.5);

          const offsetBeforeMd = this.getAttribute('data-carousel-offset-before-md')
            ? Number(this.getAttribute('data-carousel-offset-before-md'))
            : 64;
          const offsetAfterMd = this.getAttribute('data-carousel-offset-after-md')
            ? Number(this.getAttribute('data-carousel-offset-after-md'))
            : 64;
          const slidesMd = this.getSlidesPerView('data-carousel-slides-md', 3);

          const offsetBeforeLg = this.getAttribute('data-carousel-offset-before-lg')
            ? Number(this.getAttribute('data-carousel-offset-before-lg'))
            : 96;
          const offsetAfterLg = this.getAttribute('data-carousel-offset-after-lg')
            ? Number(this.getAttribute('data-carousel-offset-after-lg'))
            : 96;
          const slidesLg = this.getSlidesPerView('data-carousel-slides-lg', 4.5);

          this.instance = new swiper(this.carousel, {
            modules: [window.swiper.Scrollbar, window.swiper.Navigation],
            cssMode: this.getAttribute('data-carousel-css-mode') === 'true',
            preventClicks: this.getAttribute('data-carousel-prevent-clicks') !== 'false',
            preventClicksPropagation: this.getAttribute('data-carousel-prevent-clicks') !== 'false',
            slidesPerView: this._calculateSlidesWithOffset(slidesBase, offsetBase, offsetAfterBase),
            spaceBetween: this.getAttribute('data-carousel-gap') ? Number(this.getAttribute('data-carousel-gap')) : 12,
            slidesOffsetBefore: offsetBase,
            slidesOffsetAfter: offsetAfterBase,
            scrollbar: {
              el: this.scrollbar,
              hide: false,
              draggable: true,
            },
            navigation: {
              nextEl: this.next,
              prevEl: this.prev,
            },
            centerInsufficientSlides: this.getAttribute('data-carousel-center-insufficient-slides') === 'true',
            breakpoints: {
              600: {
                slidesPerView: this._calculateSlidesWithOffset(slidesSm, offsetBeforeSm, offsetAfterSm),
                spaceBetween: this.getAttribute('data-carousel-gap-sm')
                  ? Number(this.getAttribute('data-carousel-gap-sm'))
                  : 4,
                slidesOffsetBefore: offsetBeforeSm,
                slidesOffsetAfter: offsetAfterSm,
              },
              1200: {
                slidesPerView: this._calculateSlidesWithOffset(slidesMd, offsetBeforeMd, offsetAfterMd),
                spaceBetween: this.getAttribute('data-carousel-gap-md')
                  ? Number(this.getAttribute('data-carousel-gap-md'))
                  : 4,
                slidesOffsetBefore: offsetBeforeMd,
                slidesOffsetAfter: offsetAfterMd,
              },
              1600: {
                slidesPerView: this._calculateSlidesWithOffset(slidesLg, offsetBeforeLg, offsetAfterLg),
                spaceBetween: this.getAttribute('data-carousel-gap-lg')
                  ? Number(this.getAttribute('data-carousel-gap-lg'))
                  : 4,
                slidesOffsetBefore: offsetBeforeLg,
                slidesOffsetAfter: offsetAfterLg,
              },
            },
            on: {
              resize: (e) => {
                if (e.isLocked) {
                  this.setAttribute('data-carousel-locked', '');
                } else {
                  this.removeAttribute('data-carousel-locked');
                }

                if (this.carousel?.getAttribute('data-carousel') === 'mobile') {
                  // @ts-ignore
                  if (window.innerWidth >= 1200 && this.instance.initialized) {
                    this.instance?.disable();
                    this.instance?.detachEvents();
                    this.instance?.wrapperEl.removeAttribute('style');
                    this.instance?.slides.forEach((slide) => slide.removeAttribute('style'));
                  }
                }
              },
              slideChange: () => {
                this.images.forEach((image) => {
                  if (image && image.getAttribute('loading') !== 'eager') image.setAttribute('loading', 'eager');
                });
              },
              update: () => {
                this.syncCssModeNavigationState();
              },
            },
          });

          this.bindCssModeNavigationState();
        }

        addEventListener(
          'resize',
          () => {
            // @ts-ignore
            if (window.innerWidth < 1200 && !this.instance?.enabled) {
              this.instance?.enable();
              this.instance?.attachEvents();
            }
          },
          {
            passive: true,
          },
        );
      }

      disconnectedCallback() {
        const scrollElement = this.getCssModeScrollElement();

        if (scrollElement && this.cssModeNavigationUpdate) {
          scrollElement.removeEventListener('scroll', this.cssModeNavigationUpdate);
        }

        if (this.cssModeNavigationUpdateFrame) {
          cancelAnimationFrame(this.cssModeNavigationUpdateFrame);
        }
      }

      /**
       * @param {'next' | 'prev'} direction - The navigation direction to find
       * @returns {HTMLElement | null} The carousel navigation control
       */
      getNavigationElement(direction) {
        const internalControl = this.querySelector(`[data-carousel-${direction}]`);

        if (internalControl instanceof HTMLElement) return internalControl;

        const controlsId = this.getAttribute('data-carousel-controls');

        if (!controlsId) return null;

        const scope = this.closest('[data-carousel-scope]') || document;
        const controlGroups = Array.from(scope.querySelectorAll('[data-carousel-controls]'));

        for (const group of controlGroups) {
          if (group === this || group.getAttribute('data-carousel-controls') !== controlsId) continue;

          const control = group.querySelector(`[data-carousel-${direction}]`);

          if (control instanceof HTMLElement) return control;
        }

        return null;
      }

      bindCssModeNavigationState() {
        const scrollElement = this.getCssModeScrollElement();

        if (!scrollElement) return;

        this.cssModeNavigationUpdate = () => {
          if (this.cssModeNavigationUpdateFrame) {
            cancelAnimationFrame(this.cssModeNavigationUpdateFrame);
          }

          this.cssModeNavigationUpdateFrame = requestAnimationFrame(() => {
            this.syncCssModeNavigationState();
          });
        };

        scrollElement.addEventListener('scroll', this.cssModeNavigationUpdate, { passive: true });
        this.syncCssModeNavigationState();
      }

      getCssModeScrollElement() {
        if (this.getAttribute('data-carousel-css-mode') !== 'true') return null;

        const scrollElement = this.instance?.wrapperEl;

        if (scrollElement instanceof HTMLElement) return scrollElement;

        return null;
      }

      syncCssModeNavigationState() {
        const scrollElement = this.getCssModeScrollElement();

        if (!scrollElement || !this.next || !this.prev) return;

        const scrollPosition = Math.abs(scrollElement.scrollLeft);
        const scrollEnd = scrollElement.scrollWidth - scrollElement.clientWidth;
        const firstSnap = this.instance?.snapGrid[0] || 0;
        const lastSnap = this.instance?.snapGrid[this.instance.snapGrid.length - 1] || scrollEnd;
        const scrollStartLimit = Math.max(0, firstSnap);
        const scrollEndLimit = Math.min(scrollEnd, lastSnap);
        const tolerance = 1;

        this.setNavigationControlState(this.prev, scrollPosition - scrollStartLimit <= tolerance);
        this.setNavigationControlState(this.next, scrollEndLimit - scrollPosition <= tolerance);
      }

      /**
       * @param {HTMLElement} control - The navigation control to update
       * @param {boolean} isDisabled - Whether the control should be disabled
       */
      setNavigationControlState(control, isDisabled) {
        control.classList.toggle('swiper-button-disabled', isDisabled);
        control.toggleAttribute('disabled', isDisabled);
        control.setAttribute('aria-disabled', String(isDisabled));
      }

      /**
       * @param {string} attribute - The slides-per-view attribute name
       * @param {number} fallback - The fallback slides-per-view value
       * @returns {number | 'auto'} The slides-per-view value
       */
      getSlidesPerView(attribute, fallback) {
        const value = this.getAttribute(attribute);

        if (value === 'auto') return 'auto';
        if (value) return Number(value);

        return fallback;
      }

      /**
       * Calculate adjusted slides per view to compensate for carousel offsets
       * @param {number | 'auto'} desiredSlides - The desired number of slides per view
       * @param {number} offsetBefore - The offset before the slides in pixels
       * @param {number} offsetAfter - The offset after the slides in pixels
       * @returns {number | 'auto'} The adjusted slides per view value
       */
      _calculateSlidesWithOffset(desiredSlides, offsetBefore, offsetAfter) {
        if (desiredSlides === 'auto') return 'auto';

        const containerWidth = this.carousel?.offsetWidth || window.innerWidth;

        if (offsetBefore === 0 && offsetAfter === 0) {
          return desiredSlides;
        }

        const totalOffset = offsetBefore + offsetAfter;
        const effectiveWidthRatio = (containerWidth - totalOffset) / containerWidth;

        return desiredSlides / effectiveWidthRatio;
      }
    },
  );
}
